/**
 * Faro user meta
 *
 * The Grafana Faro / Frontend Observability dashboard associates sessions, logs,
 * events and traces with a "User" via Faro *user meta* (`faro.api.setUser(...)`).
 *
 * `@opengov/psc-react-logger` initializes its Faro instance with `isolate: true`
 * and exposes neither `setUser` nor the instance, so we cannot reach the user API
 * directly. However, Faro assigns its `api` onto every registered instrumentation
 * before calling `initialize()`. We exploit that documented contract with a tiny
 * no-op instrumentation that captures the `api`, giving us a supported handle to
 * `setUser`/`resetUser`.
 *
 * `useTraceUserAttributes` calls `setFaroUser` when the session/entity resolve.
 *
 * NOTE: `email` is PII and is sent to Grafana as user meta on every signal — only
 * set it where that exposure is acceptable.
 */

import { BaseInstrumentation, type API, type MetaUser } from '@grafana/faro-web-sdk';

let faroApi: API | undefined;

// Latest requested user, buffered so the call order between `setFaroUser` and the
// api capture doesn't matter:
//   - undefined → nothing requested yet (don't touch Faro)
//   - null      → reset requested
//   - MetaUser  → set requested
// This is essential because Auth0 sessions are cached (user known on first render)
// while Faro initializes later inside a useEffect — without buffering, the first
// setUser would be dropped and never retried (the hook's dedupe guard blocks it).
let pendingUser: MetaUser | null | undefined;

function applyUser(api: API, user: MetaUser | null): void {
  if (user === null) {
    api.resetUser();
  } else {
    api.setUser(user);
  }
}

/**
 * No-op instrumentation whose only job is to capture the Faro `api` that the SDK
 * injects at registration time, then flush any user buffered before capture.
 * Register it alongside the other instrumentations.
 */
export class FaroUserMetaInstrumentation extends BaseInstrumentation {
  readonly name = '@opengov/psp-web:user-meta';
  readonly version = '1.0.0';

  initialize(): void {
    faroApi = this.api;
    if (pendingUser !== undefined) {
      applyUser(faroApi, pendingUser);
    }
  }
}

export interface FaroUserInput {
  id?: string;
  email?: string;
  entityFriendlyId?: string;
}

/**
 * Set (or clear) the Faro user meta that powers the dashboard's User association.
 * Safe to call before Faro initializes — the value is buffered and flushed once
 * the api is captured.
 */
export function setFaroUser(input: FaroUserInput): void {
  const hasIdentity = Boolean(input.id || input.email || input.entityFriendlyId);

  let user: MetaUser | null = null;
  if (hasIdentity) {
    user = {};
    if (input.id) user.id = input.id;
    if (input.email) {
      user.email = input.email;
      // No distinct username in our identity model — use email as the username.
      user.username = input.email;
    }
    // Entity isn't a first-class Faro user field, so expose it as a user attribute.
    if (input.entityFriendlyId) {
      user.attributes = { entity_friendly_id: input.entityFriendlyId };
    }
  }

  pendingUser = user;
  if (faroApi) {
    applyUser(faroApi, user);
  }
}
